<template>
  <v-app>
    <v-progress-linear indeterminate :active="!ready"></v-progress-linear>
    <v-expand-transition>
      <v-app-bar app color="white" v-if="ready && authorized">
        <v-container class="d-flex align-center">
          <img
            v-if="this.logoPath"
            :src="require('../../assets/' + this.logoPath)"
            class="d-block mr-3 logo"
          />
          <div class="text-no-wrap text-md-h5 text-h6 ml-4 primary--text">
            {{ portalTitle.toUpperCase() }}
          </div>
          <div class="flex-fill"></div>
          <v-btn color="primary" elevation="0" @click="logout">Logout</v-btn>
        </v-container>

        <v-progress-linear
          indeterminate
          :active="loading"
          absolute
          bottom
        ></v-progress-linear>
      </v-app-bar>
    </v-expand-transition>
    <v-slide-y-transition>
      <v-main v-if="ready && authorized" class="grey lighten-2">
        <v-container class="pt-6">
          <v-scroll-y-transition mode="out-in">
            <SurveySelect
              v-if="!surveyId && surveys.length > 1"
              v-model="surveyId"
              :items="surveys"
            ></SurveySelect>
            <div v-else-if="surveyId">
              <div v-if="surveys.length > 1">
                <a href="#" text link @click.prevent.stop="surveyId = null">
                  <v-icon class="mr-1">$left</v-icon> Back
                </a>
              </div>
              <div class="d-flex align-center">
                <div class="text-h4 flex-fill text-truncate mb-4">
                  {{ survey.name }}
                </div>
              </div>
              <EntitySelect
                @edit="onEdit"
                @showContacts="onShowContacts"
                @showCodes="onShowCodes"
                :items="entities"
                :loading="loading"
              ></EntitySelect>
              <div class="mt-6">
                <div class="text-h6 font-weight-bold">Need Help?</div>
                <p>If you need assistance you can contact customer support.</p>
                <help-block
                  class="d-flex mt-4"
                  style="justify-content: space-between; width: 600px"
                />
              </div>
            </div>

            <v-alert border="left" type="error" v-else
              >No Survey(s) Found.</v-alert
            >
          </v-scroll-y-transition>
        </v-container>
      </v-main>
    </v-slide-y-transition>

    <v-dialog v-model="showLogin" max-width="400px" persistent>
      <v-card class="ma-0" :loading="loading">
        <template v-if="this.logoPath">
          <div class="pa-6 text-center">
            <img
              :src="require('../../assets/' + this.logoPath)"
              style="max-width: 350px"
            />
          </div>
        </template>
        <v-card-title v-else>{{ portalTitle }}</v-card-title>
        <ValidationObserver
          ref="loginObserver"
          tag="form"
          @submit.prevent="onLogin"
        >
          <v-card-text>
            <v-alert
              v-if="loginError"
              color="red"
              dark
              icon="$error"
              class="mb-6"
            >
              {{ loginError }}
            </v-alert>
            <ValidationProvider
              name="Email"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="login.email"
                label="Email"
                :error-messages="errors"
                outlined
                autofocus
                :disabled="loading"
              ></v-text-field>
            </ValidationProvider>
            <ValidationProvider
              name="Password"
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="login.password"
                label="Password"
                type="password"
                :error-messages="errors"
                outlined
                :disabled="loading"
              ></v-text-field>
            </ValidationProvider>
            <v-btn
              type="submit"
              block
              x-large
              color="primary"
              :disabled="loading"
              >LOGIN</v-btn
            >
          </v-card-text>
        </ValidationObserver>
      </v-card>
    </v-dialog>

    <EntityEditor ref="entityEditor" @reload="loadEntities"></EntityEditor>
    <EntityContacts
      v-if="loaded"
      ref="entityContacts"
      @reload="loadEntities"
    ></EntityContacts>
    <EntityCodes
      v-if="survey"
      ref="entityCodes"
      :survey-id="surveyId"
      :locked="!survey.enablePortalCodeManagement"
    ></EntityCodes>
    <Notifications></Notifications>
  </v-app>
</template>

<script>
import equal from "deep-is";
import surveyService from "../../services/surveyService";
import SurveySelect from "./survey-select.vue";
import HelpBlock from "../../components/HelpBlock.vue";

import Notifications from "../../components/Notifications.vue";
import EntitySelect from "./entity-select.vue";
import EntityEditor from "../admin/entity-editor.vue";
import EntityContacts from "../admin/entity-contacts.vue";
import EntityCodes from "./entity-codes.vue";
import entityService from "../../services/entityService";

import { roles, tenantClaimTypes, settings } from "../../constants/Identity.js";

export default {
  components: {
    SurveySelect,
    EntitySelect,
    EntityEditor,
    EntityCodes,
    EntityContacts,
    Notifications,
    HelpBlock,
  },
  name: "Portal",
  data: () => ({
    showLogin: false,
    pendingStateChanges: 0,
    ready: false,
    loaded: false,
    login: {
      email: "",
      password: "",
    },
    loginError: null,
    surveys: [],
    entities: [],
    surveyId: null,
  }),
  computed: {
    loading() {
      return this.pendingStateChanges > 0;
    },
    tenant() {
      return this.$store.getters["tenants/tenant"];
    },
    user() {
      return this.$store.state.user;
    },
    authorized() {
      return this.$store.getters["authorized"];
    },
    portalTitle() {
      return this.tenant?.portalTitle ?? "Survey Portal";
    },
    logoPath() {
      return this.tenant?.logoPath;
    },
    survey() {
      return this.surveys.find((x) => x.id === this.surveyId);
    },
  },
  watch: {
    tenant(val, prev) {
      if (val && val != prev) {
        this.$store.commit("setTheme", val.theme ?? "adi");

        this.$nextTick().then(() => {
          this.$store.dispatch("refreshAuth").finally(() => {
            this.ready = true;
            this.showLogin = !this.authorized;
          });
        });
      }
    },
    authorized(val) {
      this.showLogin = !val;
      if (val) this.load();
    },
    surveyId(val) {
      var query = {};
      if (val) query.surveyId = val;
      if (!equal(query, this.$route.query)) this.$router.replace({ query });
    },
  },
  methods: {
    reset() {
      this.login.password = "";
      this.loginError = null;
      this.surveys = [];
      this.entities = [];
      this.surveyId = null;
      this.$refs.loginObserver.reset();
    },
    async onLogin() {
      var isValid = await this.$refs.loginObserver.validate();
      if (!isValid) return;

      this.loginError = null;

      this.pendingStateChanges++;

      this.$store
        .dispatch("login", this.login)
        .then(
          () => {
            // Do nothing, store will handle state changes.
          },
          (error) => {
            if (error.statusCode === 400) {
              this.loginError = error.result["authorization"][0];
            } else {
              this.$store.dispatch(
                "notifications/error",
                "An unexpected error occurred while trying to login. Please try again later."
              );
            }
          }
        )
        .finally(() => {
          this.pendingStateChanges--;
        });
    },
    async load() {
      Promise.all([this.loadSurveys(), this.loadEntities()]).then(
        () => {
          this.loaded = true;
        },
        () => {
          // nothing to do here.
        }
      );
    },
    async loadEntities() {
      this.pendingStateChanges++;

      entityService
        .getAll(this.tenant.id)
        .then(
          (res) => {
            this.entities = [];

            if (this.user.role === roles.admin) this.entities = res;
            else {
              var ownerClaims = this.user.tenantClaims.filter(
                (x) => x.key === tenantClaimTypes.entityOwner
              );
              ownerClaims.forEach((claim) => {
                const pathParts = claim.value.split(
                  settings.entityPathSeparator
                );

                let searchItems = res;
                for (let i = 0; i < pathParts.length; i++) {
                  let target = searchItems.find(
                    (item) => item.id === pathParts[i]
                  );

                  // skip if not found;
                  if (!target) break;

                  if (i == pathParts.length - 1) {
                    this.entities.push(target);
                  } else {
                    if (target.children) searchItems = target.children;
                    else break; // invalid path
                  }
                }
              });
            }
          },
          (error) => {
            console.warn(error);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to load entities. Please try again later."
            );
          }
        )
        .finally(() => {
          this.pendingStateChanges--;
        });
    },
    async loadSurveys() {
      this.pendingStateChanges++;

      surveyService
        .getAll(this.tenant.id, true)
        .then(
          (res) => {
            this.surveys = res;

            if (this.$route.query.surveyId) {
              if (
                this.surveys.some((x) => x.id == this.$route.query.surveyId)
              ) {
                this.surveyId = this.$route.query.surveyId;
              } else {
                this.$router.replace({ query: {} });
              }
            }

            if (this.surveys.length == 1) {
              this.surveyId = this.surveys[0].id;
            }
          },
          (error) => {
            console.error(error);
            this.$store.dispatch(
              "notifications/error",
              "An unexpected error occurred while trying to load surveys. Please try again later."
            );
          }
        )
        .finally(() => {
          this.pendingStateChanges--;
        });
    },
    logout() {
      this.$store.dispatch("logout");
      this.reset();
    },
    onEdit({ entity, path }) {
      this.$refs.entityEditor.edit(entity, path);
    },
    onShowContacts({ entity, path }) {
      this.$refs.entityContacts.showContacts(entity, path);
    },
    onShowCodes({ entity, path }) {
      this.$refs.entityCodes.showCodes(entity, path);
    },
  },
  mounted() {
    this.pendingStateChanges++;

    this.$store
      .dispatch("setTenant", this.$route.params.tenantId)
      .then(
        () => {
          if (this.authorized) this.load();
        },
        (err) => {
          console.error(err);

          if (err.statusCode === 404)
            this.$router.replace({ name: "notFound" });
          else this.$router.replace({ name: "error:500" });
        }
      )
      .finally(() => this.pendingStateChanges--);
  },
};
</script>

<style scoped>
a.disabled {
  pointer-events: none;
  color: rgba(0, 0, 0, 0.75);
}

@media (max-width: 960px) {
  .logo {
    height: 45px !important;
  }
}

.logo {
  height: 50px;
}
</style>
